import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import DataTable from "../../../components/shared/DataTable";
import Modal from "../../../components/shared/Modal";
import SearchInput from "../../../components/shared/SearchInput";
import api from "../../../utils/api";

const EMPTY_FORM = { name: "", address: "", phone: "", email: "", affiliation: "" };

export default function Clinics() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const { data, isLoading } = useQuery(["admin-clinics", search], () =>
    api.get(`/admin/clinics?search=${search}`)
  );

  const saveMutation = useMutation(
    () =>
      editing
        ? api.put(`/admin/clinics/${editing.id}`, form)
        : api.post("/admin/clinics", form),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admin-clinics");
        toast.success("Saved");
        setShowModal(false);
        setForm(EMPTY_FORM);
        setEditing(null);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`/admin/clinics/${id}`),
    { onSuccess: () => queryClient.invalidateQueries("admin-clinics") }
  );

  const columns = [
    { key: "name", label: "Clinic Name", sortable: true },
    { key: "address", label: "Address" },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "affiliation", label: "Affiliation" },
    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div className="flex gap-2">
          <button
            onClick={() => { setEditing(row); setForm(row); setShowModal(true); }}
            className="text-xs px-3 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
          >
            Edit
          </button>
          <button
            onClick={() => deleteMutation.mutate(row.id)}
            className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200"
          >
            Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <SearchInput value={search} onChange={setSearch} placeholder="Search clinics…" className="w-72" />
        <button
          onClick={() => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); }}
          className="btn-primary"
        >
          + Add Clinic
        </button>
      </div>

      <DataTable
        columns={columns}
        data={data?.clinics || []}
        loading={isLoading}
        emptyMessage="No clinics found."
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editing ? "Edit Clinic" : "Add Clinic"}
        footer={
          <>
            <button onClick={() => setShowModal(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => saveMutation.mutate()} disabled={saveMutation.isLoading} className="btn-primary">
              {saveMutation.isLoading ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {Object.keys(EMPTY_FORM).map((field) => (
            <div key={field}>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">
                {field.charAt(0).toUpperCase() + field.slice(1)}
              </label>
              <input
                type="text"
                value={form[field]}
                onChange={(e) => setForm((f) => ({ ...f, [field]: e.target.value }))}
                className="input-field"
              />
            </div>
          ))}
        </div>
      </Modal>
    </div>
  );
}
